/* global OfficeRuntime */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getUser } from "@/api/queries";
import { AuthState } from "@/types";
import { DEBUG_MODE } from "@/utils/constants";
import { tokenCache } from "@/utils/token-cache";

export function useAuth() {
  const [accessToken, setAccessToken] = useState<string>("");
  const [authState, setAuthState] = useState<AuthState>("initializing");

  const { data: userData, error: getUserError } = useQuery({
    queryKey: ["user"],
    queryFn: getUser,
    enabled: !!accessToken,
  });

  // Get access token on mount
  useEffect(() => {
    let mounted = true;

    const getToken = async () => {
      try {
        if (!DEBUG_MODE && (typeof OfficeRuntime === "undefined" || !OfficeRuntime.auth)) {
          setAuthState("error");
          return;
        }

        setAuthState("signingIn");
        const token = await tokenCache.getAccessToken();

        if (mounted) {
          setAccessToken(token);
        }
      } catch (error: any) {
        if (mounted) {
          setAuthState("signedOut");
        }
      }
    };

    getToken();

    return () => {
      mounted = false;
    };
  }, []);

  // Update auth state based on user query result
  useEffect(() => {
    if (getUserError) {
      setAuthState(getUserError.message === "UserNotFound" ? "error" : "signedOut");
    } else if (userData) {
      setAuthState("signedIn");
    } else {
      setAuthState("signingIn");
    }
  }, [getUserError, userData]);

  return {
    accessToken,
    authState,
    userData,
  };
}
